import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Sparkles, Zap, ArrowDown, Send, Loader2, CheckCircle2, Rocket,
  MessageSquare, Mail, Clock, Users, ClipboardList, Tag, PhoneMissed,
  CalendarCheck, Star, FileText, GitBranch, AlertTriangle, HelpCircle, Webhook,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  requestAiWorkflow,
  saveAiWorkflow,
  stepTypeLabel,
  type AiWorkflowFlow,
  type AiClarification,
  type AiChatTurn,
} from '../lib/aiAutomationBuilder';
import type { AutomationV2 } from '../types/automationsV2';

interface AIAutomationBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  /** When editing an existing automation, pass the row + its current flow. */
  editAutomation?: AutomationV2 | null;
  editFlow?: AiWorkflowFlow | null;
  /** Called after a successful save so the page can refresh + open the automation. */
  onSaved: (automation: AutomationV2) => void;
}

type Phase = 'intro' | 'generating' | 'clarify' | 'review' | 'saving';

const EXAMPLES = [
  'Send a booking reminder text 24 hours before every booking',
  'Text customers back instantly when I miss their call',
  'Ask for a review 2 hours after a booking is completed',
  'Create a CRM lead from my website leads and email them right away',
];

const GENERATING_STEPS = [
  'Understanding your request…',
  'Choosing the right trigger…',
  'Building the steps…',
  'Writing your messages…',
  'Double-checking the configuration…',
];

const STEP_ICONS: Record<string, typeof Zap> = {
  booking: CalendarCheck,
  feedback: Star,
  form_submission: FileText,
  enter_crm_stage: GitBranch,
  missed_call: PhoneMissed,
  webhook: Webhook,
  sms: MessageSquare,
  email: Mail,
  delay: Clock,
  create_crm_lead: Users,
  check: CheckCircle2,
  create_task: ClipboardList,
  assign_tags: Tag,
  send_live_intake_form: FileText,
};

function StepCard({ step, isTrigger, index }: { step: AiWorkflowFlow['trigger']; isTrigger: boolean; index?: number }) {
  const Icon = STEP_ICONS[step.type] || Zap;
  const detail = (() => {
    const c = step.config as Record<string, unknown>;
    if (step.type === 'sms' || step.type === 'email') {
      const msg = String(c.message || '');
      return msg ? `“${msg.length > 160 ? `${msg.slice(0, 160)}…` : msg}”` : null;
    }
    return null;
  })();
  return (
    <div className={`rounded-2xl border p-4 ${isTrigger ? 'border-blue-200 bg-blue-50/60' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isTrigger ? 'bg-blue-600' : 'bg-gray-900'}`}>
          <Icon className="h-4.5 w-4.5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${isTrigger ? 'text-blue-600' : 'text-gray-400'}`}>
              {isTrigger ? 'Trigger' : `Step ${index}`}
            </span>
            <span className="text-[11px] text-gray-400">{stepTypeLabel(step.type)}</span>
          </div>
          <div className="text-sm font-semibold text-gray-900 mt-0.5">{step.name}</div>
          {step.summary && <p className="text-[12.5px] text-gray-600 mt-1 leading-relaxed">{step.summary}</p>}
          {detail && (
            <p className="text-[12px] text-gray-500 mt-2 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 italic whitespace-pre-wrap">
              {detail}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AIAutomationBuilderModal({
  isOpen,
  onClose,
  businessId,
  editAutomation = null,
  editFlow = null,
  onSaved,
}: AIAutomationBuilderModalProps) {
  const isEditMode = !!editAutomation;
  const [phase, setPhase] = useState<Phase>('intro');
  const [prompt, setPrompt] = useState('');
  const [flow, setFlow] = useState<AiWorkflowFlow | null>(null);
  const [genStep, setGenStep] = useState(0);
  const [refinePrompt, setRefinePrompt] = useState('');
  const [savingLive, setSavingLive] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Clarification conversation: the original request + Q&A turns, replayed to
  // the AI on each round until it returns a workflow.
  const [basePrompt, setBasePrompt] = useState('');
  const [baseMode, setBaseMode] = useState<'generate' | 'edit'>('generate');
  const [convo, setConvo] = useState<AiChatTurn[]>([]);
  const [clarification, setClarification] = useState<AiClarification | null>(null);
  const [customAnswer, setCustomAnswer] = useState('');
  /** Where to fall back if the user closes a question: intro (create) or review (refine). */
  const [clarifyReturnPhase, setClarifyReturnPhase] = useState<'intro' | 'review'>('intro');

  useEffect(() => {
    if (!isOpen) return;
    setPhase('intro');
    setPrompt('');
    setRefinePrompt('');
    setGenStep(0);
    setConvo([]);
    setClarification(null);
    setCustomAnswer('');
    // Edit mode starts from the current flow so the user can refine immediately.
    setFlow(isEditMode && editFlow ? editFlow : null);
  }, [isOpen, isEditMode, editFlow]);

  // Cycle the "creating workflow" status lines while generating.
  useEffect(() => {
    if (phase !== 'generating') return;
    setGenStep(0);
    const timer = setInterval(() => {
      setGenStep((s) => Math.min(s + 1, GENERATING_STEPS.length - 1));
    }, 1800);
    return () => clearInterval(timer);
  }, [phase]);

  if (!isOpen) return null;

  /** One round-trip to the AI. Routes the result to review / clarify / error. */
  const runRound = async (params: {
    originalPrompt: string;
    mode: 'generate' | 'edit';
    history: AiChatTurn[];
    returnPhase: 'intro' | 'review';
  }) => {
    setPhase('generating');
    const result = await requestAiWorkflow({
      businessId,
      prompt: params.originalPrompt,
      mode: params.mode,
      existingFlow: params.mode === 'edit' ? flow : undefined,
      history: params.history,
    });

    if (result.flow) {
      setFlow(result.flow);
      setConvo([]);
      setClarification(null);
      setPhase('review');
      bodyRef.current?.scrollTo({ top: 0 });
      return;
    }
    if (result.clarification) {
      const assistantTurn: AiChatTurn = {
        role: 'assistant',
        content: JSON.stringify({ needsClarification: true, question: result.clarification.question, options: result.clarification.options }),
      };
      setBasePrompt(params.originalPrompt);
      setBaseMode(params.mode);
      setConvo([...params.history, assistantTurn]);
      setClarification(result.clarification);
      setClarifyReturnPhase(params.returnPhase);
      setCustomAnswer('');
      setPhase('clarify');
      return;
    }
    if (result.insufficientTokens) {
      toast.error('Not enough AI tokens — top up in AI Settings to use the AI builder.');
    } else {
      toast.error(result.error || 'Generation failed — please try again.');
    }
    setPhase(params.returnPhase === 'review' && flow ? 'review' : 'intro');
  };

  const handleGenerate = async () => {
    const text = prompt.trim();
    if (text.length < 10) {
      toast.error('Describe what you want the automation to do (a sentence or two).');
      return;
    }
    await runRound({
      originalPrompt: text,
      mode: isEditMode && flow ? 'edit' : 'generate',
      history: [],
      returnPhase: 'intro',
    });
  };

  const handleAnswer = async (answer: string) => {
    const text = answer.trim();
    if (!text) return;
    await runRound({
      originalPrompt: basePrompt,
      mode: baseMode,
      history: [...convo, { role: 'user', content: text }],
      returnPhase: clarifyReturnPhase,
    });
  };

  const handleRefine = async () => {
    const text = refinePrompt.trim();
    if (!text || !flow) return;
    setRefinePrompt('');
    await runRound({ originalPrompt: text, mode: 'edit', history: [], returnPhase: 'review' });
  };

  const handleSave = async (goLive: boolean) => {
    if (!flow || phase === 'saving') return;
    setPhase('saving');
    setSavingLive(goLive);
    try {
      const automation = await saveAiWorkflow({
        businessId,
        flow,
        automation: editAutomation,
        goLive,
      });
      toast.success(goLive ? 'Automation is live!' : 'Automation saved as draft');
      onSaved(automation);
      onClose();
    } catch (e) {
      console.error('AI automation save failed:', e);
      toast.error(e instanceof Error ? e.message : 'Could not save the automation');
      setPhase('review');
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100050] bg-black/50 flex items-center justify-center p-3 sm:p-6">
      <div className="bg-white w-full max-w-2xl max-h-[92vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
              <Sparkles className="h-4.5 w-4.5 text-white" />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-gray-900">
                {isEditMode ? 'Edit with AI' : 'AI Automation Builder'}
              </h2>
              <p className="text-[12px] text-gray-500">
                {isEditMode ? editAutomation?.name : 'Describe it — the AI builds the whole workflow'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400" disabled={phase === 'saving'}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto">
          {/* ── Intro: how automations work + prompt ── */}
          {phase === 'intro' && (
            <div className="p-5 space-y-5">
              {!isEditMode && (
                <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50/60 border border-blue-100 p-4">
                  <h3 className="text-[13.5px] font-bold text-gray-900 mb-2">How automations work</h3>
                  <p className="text-[12.5px] text-gray-600 leading-relaxed">
                    Every automation starts with a <span className="font-semibold text-gray-800">trigger</span> — something that
                    happens in Nexus (a booking is created, a form is submitted, a call is missed). Then a chain of{' '}
                    <span className="font-semibold text-gray-800">actions</span> runs automatically: send a text or email, wait a
                    set time, create a CRM lead, tag the customer, and more. Once you press{' '}
                    <span className="font-semibold text-gray-800">Go Live</span>, it runs on its own — every time, without you
                    lifting a finger.
                  </p>
                </div>
              )}

              {isEditMode && flow && (
                <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4">
                  <h3 className="text-[13px] font-bold text-gray-900 mb-1.5">Current workflow</h3>
                  <p className="text-[12.5px] text-gray-600">
                    {stepTypeLabel(flow.trigger.type)} trigger → {flow.actions.map((a) => stepTypeLabel(a.type)).join(' → ')}
                  </p>
                  <p className="text-[12px] text-gray-500 mt-1.5">
                    Tell the AI what to change — it can rewrite messages, add or remove steps, change timing, or redesign the flow.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-[13px] font-semibold text-gray-800 mb-2">
                  {isEditMode ? 'What would you like to change?' : 'What should this automation do?'}
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={3}
                  placeholder={
                    isEditMode
                      ? 'e.g. Make the reminder send 48 hours before instead, and make the text friendlier'
                      : 'e.g. Send a booking reminder text 24 hours before every booking'
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 resize-none"
                />
              </div>

              {!isEditMode && (
                <div>
                  <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Popular automations</div>
                  <div className="flex flex-wrap gap-2">
                    {EXAMPLES.map((ex) => (
                      <button
                        key={ex}
                        onClick={() => setPrompt(ex)}
                        className="text-[12px] px-3 py-1.5 rounded-full border border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-colors text-left"
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleGenerate}
                disabled={prompt.trim().length < 10}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 font-semibold text-white transition-colors"
              >
                <Sparkles className="h-4 w-4" />
                {isEditMode ? 'Update workflow with AI' : 'Build my automation'}
              </button>
              <p className="text-center text-[11px] text-gray-400 -mt-2">Uses AI tokens like other AI features</p>
            </div>
          )}

          {/* ── Generating animation ── */}
          {phase === 'generating' && (
            <div className="p-10 flex flex-col items-center justify-center min-h-[380px]">
              <div className="relative mb-8">
                <div className="w-20 h-20 rounded-3xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
                  <Zap className="h-9 w-9 text-white" />
                </div>
                <div className="absolute -inset-2 rounded-[28px] border-2 border-blue-400/40 animate-ping" />
              </div>
              <h3 className="text-[16px] font-bold text-gray-900 mb-6">Creating your workflow</h3>
              <div className="space-y-2.5 w-full max-w-xs">
                {GENERATING_STEPS.map((label, i) => (
                  <div
                    key={label}
                    className={`flex items-center gap-2.5 text-[13px] transition-all duration-500 ${
                      i < genStep ? 'text-gray-400' : i === genStep ? 'text-gray-900 font-medium' : 'text-gray-300'
                    }`}
                  >
                    {i < genStep ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                    ) : i === genStep ? (
                      <Loader2 className="h-4 w-4 text-blue-600 animate-spin shrink-0" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border border-gray-200 shrink-0" />
                    )}
                    {label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Clarify: the AI asks a guided question before building ── */}
          {phase === 'clarify' && clarification && (
            <div className="p-5 space-y-4">
              <div className="rounded-2xl bg-gray-50 border border-gray-200 px-4 py-3">
                <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Your request</div>
                <p className="text-[12.5px] text-gray-700">{basePrompt}</p>
              </div>

              <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
                    <HelpCircle className="h-4.5 w-4.5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-blue-600 mb-1">Quick question first</div>
                    <p className="text-[13.5px] font-semibold text-gray-900 leading-snug">{clarification.question}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {clarification.options.map((opt) => (
                  <button
                    key={opt.label}
                    onClick={() => void handleAnswer(opt.label)}
                    className="w-full text-left rounded-xl border border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50 transition-colors px-4 py-3 group"
                  >
                    <div className="text-[13px] font-semibold text-gray-900 group-hover:text-blue-700">{opt.label}</div>
                    {opt.detail && <div className="text-[12px] text-gray-500 mt-0.5 leading-relaxed">{opt.detail}</div>}
                  </button>
                ))}
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-3">
                <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
                  Or answer in your own words
                </div>
                <div className="flex items-end gap-2">
                  <textarea
                    value={customAnswer}
                    onChange={(e) => setCustomAnswer(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleAnswer(customAnswer); }
                    }}
                    rows={1}
                    placeholder="Type your answer…"
                    className="flex-1 px-3.5 py-2.5 border border-gray-300 rounded-xl text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
                  />
                  <button
                    onClick={() => void handleAnswer(customAnswer)}
                    disabled={!customAnswer.trim()}
                    className="p-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white transition-colors shrink-0"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <button
                onClick={() => setPhase(clarifyReturnPhase === 'review' && flow ? 'review' : 'intro')}
                className="w-full text-[12px] font-medium text-gray-400 hover:text-gray-600 py-1 transition-colors"
              >
                ← Go back
              </button>
            </div>
          )}

          {/* ── Review: recap each step + refine chat ── */}
          {(phase === 'review' || phase === 'saving') && flow && (
            <div className="p-5 space-y-4">
              <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50/60 border border-blue-100 p-4">
                <div className="text-[14px] font-bold text-gray-900">{flow.name}</div>
                {flow.overview && <p className="text-[12.5px] text-gray-600 mt-1 leading-relaxed">{flow.overview}</p>}
              </div>

              {flow.notes.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-1">
                  {flow.notes.map((n, i) => (
                    <div key={i} className="flex items-start gap-2 text-[12px] text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{n}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-1.5">
                <StepCard step={flow.trigger} isTrigger />
                {flow.actions.map((a, i) => (
                  <div key={a.id}>
                    <div className="flex justify-center py--1">
                      <ArrowDown className="h-4 w-4 text-gray-300" />
                    </div>
                    <StepCard step={a} isTrigger={false} index={i + 1} />
                  </div>
                ))}
              </div>

              {/* Refine chat */}
              <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-3">
                <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
                  Want changes? Just ask
                </div>
                <div className="flex items-end gap-2">
                  <textarea
                    value={refinePrompt}
                    onChange={(e) => setRefinePrompt(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleRefine(); }
                    }}
                    rows={1}
                    placeholder='e.g. "Make it 48 hours before" or "add an email follow-up too"'
                    className="flex-1 px-3.5 py-2.5 border border-gray-300 rounded-xl text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
                    disabled={phase === 'saving'}
                  />
                  <button
                    onClick={handleRefine}
                    disabled={!refinePrompt.trim() || phase === 'saving'}
                    className="p-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white transition-colors shrink-0"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {(phase === 'review' || phase === 'saving') && flow && (
          <div className="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              onClick={() => handleSave(false)}
              disabled={phase === 'saving'}
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {phase === 'saving' && !savingLive ? 'Saving…' : 'Save as draft'}
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={phase === 'saving'}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-semibold text-white transition-colors disabled:opacity-50"
            >
              <Rocket className="h-4 w-4" />
              {phase === 'saving' && savingLive ? 'Going live…' : 'Save & Go Live'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
